import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["questionKey"];
import { CardGrid } from 'foundations-components/transitional/components/CardGrid';
import { View } from 'foundations-components/transitional/components/View';
import { PPUIPageHeader } from 'pricing-pages-ui-components/semantic/PageHeader';
import { PageSection } from 'pricing-pages-ui-components/semantic/PageSection';
import { useQueryParamTerm, useQueryParamsEffect } from 'pricing-pages-ui/utils/urls/appQueryParamUtils';
import Raven from 'raven-js';
import { useEffect, useState } from 'react';
import { buildFilteredHighlightedProducts } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import BannerSection from '../components/banners/BannerSection';
import IntegrationsSection from '../components/common/IntegrationsSection';
import PhoneSupportFooter from '../components/common/PhoneSupportFooter';
import ProductPageSubtitle from '../components/common/ProductPageSubtitle';
import ProductPageTitle from '../components/common/ProductPageTitle';
import FailedLoadBanner from '../components/common/loading/FailedLoadBanner';
import FallbackErrorBanner from '../components/common/loading/FallbackErrorBanner';
import Faq from '../components/faqs/Faq';
import FooterMicrocopy from '../components/faqs/FooterMicrocopy';
import Question from '../components/faqs/Question';
import ProductFeatureTable from '../components/feature-table/ProductFeatureTable';
import MobileHighlightedFreeCard from '../components/mobile/MobileHighlightedFreeCard';
import MobileHighlightedMerchCard from '../components/mobile/MobileHighlightedMerchCard';
import MobileProductLevelSelectorButtons from '../components/mobile/MobileProductLevelSelectorButtons';
import MobileWrapper from '../components/mobile/containers/MobileWrapper';
import { FaqsConfig } from '../constants/faqs/FaqsConfig';
import { getMobileMerchCardsToShow } from '../constants/merchCards/MerchCardsToShow';
import { SCREENS } from '../constants/tracking/Screens';
import { TRACKING_SOURCES } from '../constants/tracking/TrackingSources';
import { ProductPageContext } from '../context/ProductPageContext';
import { useAnchorLinkScroll } from '../hooks/htmlElementHooks';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useTrackRetail } from '../hooks/useTrackRetail';
import { useAppSelector } from '../reducers';
import { hasPricingAndPurchasingFailedSelector } from '../selectors/LoadingStateSelectors';
import { navItemForProductLineSelector } from '../selectors/RoutingSelectors';
import { trackPageView } from '../utils/tracking/trackingEventUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const gridItemProps = {
  gridColumnStart: 1,
  paddingInline: '700',
  showBorderLeft: true,
  showBorderRight: true
};
const MobileProductPage = ({
  productLine
}) => {
  useAnchorLinkScroll();
  useTrackRetail();
  useDocumentTitle(productLine.toLowerCase());
  const hasPricingAndPurchasingFailed = useAppSelector(hasPricingAndPurchasingFailedSelector);
  const navItem = useAppSelector(state => navItemForProductLineSelector(state, productLine));
  const faqsConfig = FaqsConfig[navItem];
  const screen = SCREENS[productLine];
  useEffect(() => {
    if (!screen) {
      Raven.captureMessage('ProductPage screen is undefined', {
        extra: {
          productLine
        }
      });
      return;
    }
    trackPageView({
      screen
    });
  }, [productLine, screen]);
  const productLevels = getMobileMerchCardsToShow();
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  const [selectedProductLevel, setSelectedProductLevel] = useState(productLevels[0]);
  const filteredHighlightedProducts = buildFilteredHighlightedProducts({
    productLevels: [selectedProductLevel],
    productLine
  });
  return /*#__PURE__*/_jsx(MobileWrapper, {
    navMarkerName: `${productLine}_PAGE`,
    navItem: navItem,
    children: /*#__PURE__*/_jsxs(ProductPageContext.Provider, {
      value: {
        highlightedProducts: filteredHighlightedProducts
      },
      children: [/*#__PURE__*/_jsx(BannerSection, {
        productLine: productLine,
        screen: screen
      }), /*#__PURE__*/_jsx(FailedLoadBanner, {
        hasFailed: hasPricingAndPurchasingFailed
      }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsxs(PPUIPageHeader, {
        children: [/*#__PURE__*/_jsx(ProductPageTitle, {
          productLine: productLine
        }), /*#__PURE__*/_jsx(ProductPageSubtitle, {
          productLine: productLine
        })]
      }), /*#__PURE__*/_jsx(MobileProductLevelSelectorButtons, {
        productLevels: productLevels,
        onClick: setSelectedProductLevel,
        selectedProductLevel: selectedProductLevel
      }), /*#__PURE__*/_jsxs(PageSection, {
        children: [/*#__PURE__*/_jsx(View, {
          marginBlockStart: "500",
          children: /*#__PURE__*/_jsx(CardGrid, {
            columns: 1,
            rowGap: "0",
            children: selectedProductLevel === 'FREE' ? /*#__PURE__*/_jsx(MobileHighlightedFreeCard, {
              productLine: productLine,
              gridItemProps: gridItemProps
            }) : /*#__PURE__*/_jsx(MobileHighlightedMerchCard, {
              productLevel: selectedProductLevel,
              productLine: productLine,
              gridItemProps: gridItemProps
            })
          })
        }), /*#__PURE__*/_jsx(ProductFeatureTable, {
          productLine: productLine,
          highlightedProducts: filteredHighlightedProducts,
          screen: screen
        }, productLine)]
      }), /*#__PURE__*/_jsx(IntegrationsSection, {}), /*#__PURE__*/_jsxs(Faq, {
        children: [faqsConfig.map(_ref => {
          let {
              questionKey
            } = _ref,
            passthroughOptions = _objectWithoutPropertiesLoose(_ref, _excluded);
          return /*#__PURE__*/_jsx(Question, Object.assign({
            questionKey: questionKey
          }, passthroughOptions), questionKey);
        }), /*#__PURE__*/_jsx(FooterMicrocopy, {})]
      }), /*#__PURE__*/_jsx(PhoneSupportFooter, {
        phoneSourceId: TRACKING_SOURCES[`${productLine}_FOOTER`] || TRACKING_SOURCES.MOBILE_PRODUCT_PAGE,
        screen: screen,
        upgradeProduct: undefined
      })]
    })
  });
};
export default MobileProductPage;